import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LangContext";
import Reveal from "@/components/Reveal";
import { MoveRight } from "lucide-react";

const SERVICES = [
  {
    id: "carpentry",
    num: "01",
    image: "https://images.pexels.com/photos/8146143/pexels-photo-8146143.jpeg",
    items_en: [
      "Bespoke joinery & cabinetry",
      "Shop fittings & retail interiors",
      "Restoration of period furniture",
      "Built-in bookshelves",
      "Entertainment centres for television",
      "Walk-in wardrobes & dressing rooms",
      "Interior decoration & wood detailing",
    ],
    items_es: [
      "Ebanistería y cabinetry a medida",
      "Shop fittings e interiores retail",
      "Restauración de muebles de época",
      "Bookshelves empotrados",
      "Centros de entretenimiento para televisor",
      "Walk-in closets y vestidores",
      "Decoración interior y detalle en madera",
    ],
  },
  {
    id: "construction",
    num: "02",
    image: "https://images.pexels.com/photos/18729404/pexels-photo-18729404.jpeg",
    items_en: [
      "Whole-house remodels",
      "Cottage restorations",
      "Granny annexes & garden cabins",
      "Structural refits & extensions",
      "Period property restoration",
      "Kitchen & bathroom rebuilds",
    ],
    items_es: [
      "Remodelaciones de casa completa",
      "Restauración de cottages",
      "Granny annexes y cabinas de jardín",
      "Refits estructurales y extensiones",
      "Restauración de inmuebles históricos",
      "Reconstrucciones de cocina y baño",
    ],
  },
  {
    id: "consult",
    num: "03",
    image: "https://images.pexels.com/photos/13727724/pexels-photo-13727724.png",
    items_en: [
      "Bespoke carpentry consultation (60 min · £120)",
      "Private construction consultation (75 min · £180)",
      "3D design & pre-visualization (60 min · £150)",
      "Material & supplier recommendations",
      "Feasibility & cost guidance",
    ],
    items_es: [
      "Consultoría de ebanistería a medida (60 min · £120)",
      "Consultoría de construcción privada (75 min · £180)",
      "Diseño 3D y pre-visualización (60 min · £150)",
      "Recomendaciones de materiales y proveedores",
      "Viabilidad y orientación de costes",
    ],
  },
];

export default function Services() {
  const { lang, t } = useLang();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [location.hash]);

  return (
    <div data-testid="page-services" className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-24">
      <section className="pt-20 pb-16">
        <Reveal>
          <p className="text-eyebrow text-brand mb-6">{t("services.eyebrow")}</p>
          <h1 className="font-display font-light text-5xl sm:text-6xl md:text-7xl text-ink leading-none tracking-tighter">
            {t("services.title")}
          </h1>
        </Reveal>
      </section>

      <div className="space-y-32 pb-32">
        {SERVICES.map((s, idx) => (
          <section
            key={s.id}
            id={s.id}
            data-testid={`services-section-${s.id}`}
            className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start scroll-mt-32"
          >
            <Reveal className={`md:col-span-6 ${idx % 2 === 1 ? "md:order-2" : ""}`} y={40}>
              <div className="relative overflow-hidden h-[520px] border border-edge group">
                <motion.img
                  src={s.image}
                  alt=""
                  className="w-full h-full object-cover"
                  initial={{ scale: 1.08 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                  viewport={{ once: true }}
                />
              </div>
            </Reveal>
            <Reveal className={`md:col-span-6 ${idx % 2 === 1 ? "md:order-1" : ""} md:pt-12`} y={40} delay={0.15}>
              <p className="text-eyebrow text-brand mb-4">{s.num}</p>
              <h2 className="font-display font-light text-4xl sm:text-5xl text-ink mb-3 tracking-tight">
                {t(`services.${s.id}.title`)}
              </h2>
              <p className="text-eyebrow text-ink-muted mb-8">
                {t(`services.${s.id}.tag`)}
              </p>
              <p className="text-ink-muted leading-relaxed mb-10">
                {t(`services.${s.id}.body`)}
              </p>
              <ul className="space-y-3 mb-10 border-t border-edge">
                {(lang === "en" ? s.items_en : s.items_es).map((it, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-4 py-3 border-b border-edge text-ink"
                  >
                    <span className="text-eyebrow text-brand mt-1">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm">{it}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={s.id === "consult" ? "/consultations" : "/contact"}
                data-testid={`services-cta-${s.id}`}
                className="inline-flex items-center gap-4 px-6 py-4 bg-brand text-ink-inverse hover:bg-brand-hover transition-colors group"
              >
                <span className="text-sm font-medium tracking-wide">
                  {s.id === "consult" ? t("nav.bookNow") : (lang === "en" ? "Request a quote" : "Solicitar presupuesto")}
                </span>
                <MoveRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </section>
        ))}
      </div>
    </div>
  );
}
